import { useAuth } from '@/lib/auth/react';
import { BottomSheet } from '@/components/ui/bottom-sheet';
import { Dialog } from '@/components/ui/dialog';
import { useIsDesktop } from '@/lib/notifications/useIsDesktop';
import {
  useAccountStatusGateOpen,
  useAccountStatusGateCode,
  useCloseAccountStatusGate,
} from '@/store/accountStatusGateStore';

/** Open the Intercom messenger. Available once the messenger has booted (the
 *  SDK injects the global `Intercom` fn). A no-op if it hasn't booted yet. */
function openSupportChat() {
  (window as Window & { Intercom?: (command: string) => void }).Intercom?.('show');
}

/** Brand-gradient CTA pill per the account-blocked Figma (Button - Big). */
const primaryCtaClasses = [
  'h-[50px] w-[270px] max-w-full rounded-pill',
  'text-2xl font-semibold capitalize text-white',
  'bg-[linear-gradient(139deg,var(--color-brand-grad-3)_0%,var(--color-brand-grad-2)_50%,var(--color-brand-grad-1)_100%)]',
  'shadow-[0_1.818px_1.818px_rgba(0,0,0,0.15),inset_0_2px_4px_rgba(255,255,255,0.3)]',
  'cursor-pointer transition-opacity hover:opacity-90 active:opacity-80',
].join(' ');

/**
 * Account-blocked screen (CP-007b). Mount once in the app shell. Opened by the
 * 403 ACCOUNT_CLOSED / ACCOUNT_LOCKED interceptor when the server blocks a
 * suspended (closed) or banned (locked) account. Non-dismissable — a blocked
 * account can't use the app.
 *
 * A CLOSED (suspended) account keeps support chat: it can log in (the session is
 * chat-scoped by the guard), the Intercom messenger boots, and this screen
 * surfaces a "Contact Support" action. A LOCKED (banned) account has no chat —
 * only log out.
 *
 * Renders as a bottom sheet on mobile and a centered dialog on desktop (same
 * split as NotificationDetail), matching the account-closed Figma designs.
 */
export function AccountStatusGate() {
  const open = useAccountStatusGateOpen();
  const code = useAccountStatusGateCode();
  const close = useCloseAccountStatusGate();
  const { logout } = useAuth();
  const isDesktop = useIsDesktop();

  if (!open || !code) return null;

  // Dismiss the gate as we log out, then hard-navigate to the lander — the
  // full refresh clears every in-memory state left by the blocked session
  // (same rationale as the settings logout).
  const handleLogout = () => {
    close();
    void logout().finally(() => window.location.assign('/'));
  };

  const locked = code === 'ACCOUNT_LOCKED';
  const title = locked ? 'Account is locked' : 'Account is closed';
  const body = locked
    ? 'Access to your account has been locked. If you think this is a mistake, please contact support.'
    : 'This account is currently restricted. Reopening is subject to Crown Prediction review and approval is not guaranteed.';

  const content = (
    <div className="flex flex-col items-center gap-10 px-4 pb-12 pt-10 text-center lg:py-[60px]">
      <div className="flex flex-col items-center gap-3.5">
        <h2 className="text-2xl font-medium text-primary">{title}</h2>
        <p className="max-w-[339px] text-xl text-primary">{body}</p>
      </div>
      <div className="flex w-full flex-col items-center gap-1">
        {locked ? (
          <button type="button" onClick={handleLogout} className={primaryCtaClasses}>
            Log Out
          </button>
        ) : (
          <>
            <button type="button" onClick={openSupportChat} className={primaryCtaClasses}>
              Contact Support
            </button>
            <button
              type="button"
              onClick={handleLogout}
              className="cursor-pointer text-2xl leading-[34px] text-purple underline hover:opacity-80"
            >
              Log Out
            </button>
          </>
        )}
      </div>
    </div>
  );

  // No-op onClose: the block is server-enforced, so the screen is not dismissable.
  if (!isDesktop) {
    return (
      // The built-in handle is hidden because it drags-to-dismiss; a blocked
      // account must not be able to swipe the gate away. A static pill stands
      // in for it so the sheet still reads as a sheet, per the design.
      <BottomSheet open onClose={() => {}} hideHandle size="sm">
        <div className="flex justify-center pt-1" aria-hidden="true">
          <div className="h-1 w-12 rounded-full bg-border" />
        </div>
        {content}
      </BottomSheet>
    );
  }

  return (
    <Dialog
      open
      onClose={() => {}}
      className="max-w-[450px] rounded-[20px] border border-border p-0 lg:max-w-[450px] lg:p-0"
    >
      {content}
    </Dialog>
  );
}
